/**
 * ChromeExtensionPage — placeholder surface for the upcoming Chrome
 * Extension (Phase 12+).
 *
 * Renders an identical "Coming soon" state for BOTH demo and signed-in
 * users for now. When the extension goes live, gating logic flips:
 *   • Signed-in users: see the install CTA + post-install pairing flow
 *   • Demo users:      see the "available to signed-in users" lock
 *
 * Why this lives in the consumer app, not in /admin: the extension is
 * a consumer feature (price-paste, smart-suggest, capture). It just
 * needs a permanent informational page so users can find install
 * instructions + report issues.
 */
import { Link } from 'react-router-dom';
import {
  Chrome, Lock, Sparkles, ShieldCheck, Zap, ScanLine,
  Calculator, Bell, LogIn, Mail, ExternalLink,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

// Toggle to `true` ONLY when the extension is approved in the Chrome Web
// Store and we're ready to enable the install CTA for signed-in users.
// Demo users always see the "coming soon / signed-in only" gate.
const EXTENSION_LIVE = false;

const FEATURE_CARDS = [
  {
    icon: Zap,
    title: 'Smart card at checkout',
    body: 'At checkout, we surface the best card to use for that merchant — using your own reward rules and current milestones.',
  },
  {
    icon: ScanLine,
    title: 'Auto-capture transactions',
    body: 'Online purchases on Amazon, Flipkart, Swiggy, Uber, and 50+ sites are captured into your CardAssist Online log without you lifting a finger.',
  },
  {
    icon: Calculator,
    title: 'Live reward math',
    body: 'See the exact reward you\u2019ll earn (₹ value, not just points) before you click pay.',
  },
  {
    icon: Bell,
    title: 'Milestone nudges',
    body: 'A small heads-up when you\u2019re ₹3,000 away from unlocking a Taj voucher, or ₹15K from a fee waiver.',
  },
];

export default function ChromeExtensionPage() {
  const { user } = useAuth();
  const isDemo = !!user?.is_demo;

  // Three possible states:
  //   • EXTENSION_LIVE=false → "Coming soon" for everyone
  //   • EXTENSION_LIVE=true, isDemo=true → "Signed-in users only" gate
  //   • EXTENSION_LIVE=true, signed-in → install CTA + pairing flow
  const showComingSoon = !EXTENSION_LIVE;
  const showDemoLock = EXTENSION_LIVE && isDemo;
  const showInstall  = EXTENSION_LIVE && !isDemo;

  return (
    <div className="space-y-6" data-testid="chrome-extension-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className={cn(
            'flex items-center gap-2 text-xs uppercase tracking-wider',
            showComingSoon ? 'text-muted-foreground' : 'text-accent',
          )}>
            {showComingSoon ? <Sparkles className="h-3.5 w-3.5" /> : <Chrome className="h-3.5 w-3.5" />}
            {showComingSoon ? 'Coming soon · Beta' : 'Chrome Extension · Beta'}
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">CardAssist Online for Chrome</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            A small Chrome extension that picks the right card at checkout, captures online
            transactions to your log, and nudges you toward milestones — without ever leaving the
            site you&rsquo;re on.
          </p>
        </div>
      </div>

      {/* ── State 1 — Coming soon (everyone) ─────────────────────────── */}
      {showComingSoon && (
        <Card className="p-8 border border-dashed border-border" data-testid="chrome-extension-coming-soon">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--surface-2))] grid place-items-center shrink-0">
              <Chrome className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-[280px] max-w-2xl">
              <div className="font-display text-xl font-semibold">Coming soon to the Chrome Web Store</div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                The extension is in final review with Google. We&rsquo;ll enable it here for
                signed-in users the moment it&rsquo;s approved. Demo accounts will continue to see
                this preview page.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                <Badge variant="outline" className="gap-1 text-emerald-600 border-emerald-500/30 bg-emerald-500/5">
                  <ShieldCheck className="h-3 w-3" /> No card numbers / passwords read
                </Badge>
                <Badge variant="outline">Open-source manifest</Badge>
                <Badge variant="outline">Off-by-default per site</Badge>
              </div>
            </div>
            {isDemo && (
              <div className="ml-auto flex flex-col items-end gap-2 max-w-xs">
                <Badge variant="outline" className="gap-1 text-muted-foreground border-border">
                  <Lock className="h-3 w-3" /> Signed-in users only at launch
                </Badge>
                <Button asChild size="sm" variant="outline" className="gap-1.5" data-testid="chrome-extension-demo-signin">
                  <Link to="/login"><LogIn className="h-3.5 w-3.5" /> Sign in</Link>
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* ── State 2 — Demo lock (post-launch) ────────────────────────── */}
      {showDemoLock && (
        <Card className="p-8 border border-dashed border-border" data-testid="chrome-extension-demo-locked">
          <div className="flex items-start gap-5 flex-wrap">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--surface-2))] grid place-items-center shrink-0">
              <Chrome className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-[280px] max-w-2xl">
              <div className="font-display text-xl font-semibold">
                Chrome Extension is available only for signed-in users.
              </div>
              <p className="mt-1.5 text-sm text-muted-foreground">
                The demo account is sandboxed and cannot install third-party extensions or pair
                with a personal browser. Create a free CardAssist Online account to get the
                smart-checkout experience.
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button asChild className="gap-2 h-11" data-testid="chrome-extension-demo-signup">
                <Link to="/signup"><Sparkles className="h-4 w-4" /> Create free account</Link>
              </Button>
              <Button asChild variant="outline" className="gap-2 h-11" data-testid="chrome-extension-demo-signin-btn">
                <Link to="/login"><LogIn className="h-4 w-4" /> Sign in</Link>
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── State 3 — Install CTA (signed-in, post-launch) ───────────── */}
      {showInstall && (
        <Card className="p-6 border-accent/30 bg-accent/5" data-testid="chrome-extension-install">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="h-12 w-12 rounded-xl bg-accent/15 grid place-items-center text-accent shrink-0">
              <Chrome className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="font-display text-lg font-semibold">Install the extension</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Add it to Chrome from the Web Store, then sign in inside the extension to pair it
                with your CardAssist Online account.
              </p>
            </div>
            <Button asChild size="lg" className="gap-2 h-11" data-testid="chrome-extension-install-btn">
              <a href="#" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4" /> Add to Chrome
              </a>
            </Button>
          </div>
        </Card>
      )}

      {/* What it does — visible to everyone */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-3">What it does</h2>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="chrome-extension-feature-grid">
          {FEATURE_CARDS.map((f, i) => {
            const Icon = f.icon;
            return (
              <Card key={i} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-[hsl(var(--surface-2))] grid place-items-center shrink-0 text-accent">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-sm font-semibold">{f.title}</div>
                    <p className="text-xs text-muted-foreground mt-1">{f.body}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      <Card className="p-4" data-testid="chrome-extension-privacy">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">Privacy.</span>{' '}
            The extension never reads your card number, CVV, or passwords. It only reads visible
            HTML on supported checkout pages and the URL you&rsquo;re on. You can pause it per-site
            or fully disable it from <code className="font-mono-num text-foreground">chrome://extensions</code>.
            See our <Link to="/legal/privacy" className="underline">privacy policy</Link> for details.
          </div>
        </div>
      </Card>
    </div>
  );
}
